/**
 * @file DocumentsFilterBar.tsx
 * @description Фильтры реестра документов (phase19.31, часть 3): поиск по
 * номеру или контрагенту · «Вид ▾» · «Статус ▾» одной строкой от 1024.
 *
 * До 1024 в строке только поиск и «Фильтры · N»: вид и статус уходят в
 * нижнюю шторку. Поиск применяется сам через 0,4 с после ввода (и сразу по
 * Enter). Вид и статус — `SelectField`, не нативные списки. Сервер ищет по
 * номеру документа и контрагенту (контракт §19) — подсказка в поле об этом.
 */

import { useEffect, useMemo, useState } from "react";

import { DOCUMENT_KINDS, DOCUMENT_STATUSES } from "./document-view";
import { documentsSheetFilterCount, type DocumentsRegistryQuery } from "./documents-query";
import { FilterBar } from "../../ui/FilterBar";
import { FilterSheetButton } from "../../ui/FilterSheetButton";
import { SearchInput } from "../../ui/SearchInput";
import { SelectField } from "../../ui/SelectField";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { DOCUMENT_KIND_LABEL, DOCUMENT_STATUS_LABEL, type DocumentKind, type DocumentStatus } from "../../../lib/api-crm";

/** Задержка поиска: не дёргаем сервер на каждую букву. */
const SEARCH_DELAY_MS = 400;

const KIND_OPTIONS = [{ value: "", label: "Все виды" }, ...DOCUMENT_KINDS.map((k) => ({ value: k, label: DOCUMENT_KIND_LABEL[k] }))];
const STATUS_OPTIONS = [{ value: "", label: "Все статусы" }, ...DOCUMENT_STATUSES.map((s) => ({ value: s, label: DOCUMENT_STATUS_LABEL[s] }))];

interface Props {
  query: DocumentsRegistryQuery;
  onChange: (patch: Partial<DocumentsRegistryQuery>) => void;
}

export function DocumentsFilterBar({ query, onChange }: Props) {
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const [search, setSearch] = useState(query.search);

  // Адрес поменялся извне («Сбросить фильтры», «назад») — поле за ним.
  useEffect(() => setSearch(query.search), [query.search]);

  useEffect(() => {
    if (search.trim() === query.search) return;
    const timer = window.setTimeout(() => onChange({ search: search.trim() }), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search, query.search, onChange]);

  const selectCls = compact ? "w-full" : "w-[240px]";
  const controls = useMemo(
    () => (
      <>
        <SelectField
          ariaLabel="Вид документа"
          value={query.kind}
          onChange={(kind) => onChange({ kind: kind as "" | DocumentKind })}
          options={KIND_OPTIONS}
          placeholder="Все виды"
          className={selectCls}
        />
        <SelectField
          ariaLabel="Статус документа"
          value={query.status}
          onChange={(status) => onChange({ status: status as "" | DocumentStatus })}
          options={STATUS_OPTIONS}
          placeholder="Все статусы"
          className={compact ? "w-full" : "w-[180px]"}
        />
      </>
    ),
    [query.kind, query.status, onChange, selectCls, compact],
  );

  return (
    <FilterBar
      search={
        <form
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            onChange({ search: search.trim() });
          }}
        >
          <SearchInput value={search} onChange={setSearch} placeholder="Номер или контрагент" ariaLabel="Поиск по номеру документа или контрагенту" />
        </form>
      }
    >
      {compact ? (
        <FilterSheetButton count={documentsSheetFilterCount(query)} onReset={() => onChange({ kind: "", status: "" })}>
          {controls}
        </FilterSheetButton>
      ) : (
        controls
      )}
    </FilterBar>
  );
}
